import pLimit from 'p-limit';
import { voidProgress } from "@archipad-js/core/async";
import { firstValueFrom, Observable, of } from "rxjs";
import { BaseProjectContext, ProjectContextDataManager, ProjectRepository } from "@archipad/backend/project/baseProjectContext";
import { ProgressObserver } from "@archipad-js/core/progress";
import { task } from "@core/tasks/generator";
import { Logger } from "@archipad-js/core/logger";
import { SimpleProjectContextManager } from "@archipad/backend/project/simpleProjectContext";

import { FileService } from "@core/services/file/file";
import { PatchUserInfo } from "@archipad-js/core/patch";
import { DOWNLOAD_POLICY } from '@archipad-js/archipad/entity-services';
import { isNotNullish } from '@archipad-js/core/utils';

export type PreCacheEvent = PreloadProjectAttachmentsEvent;

export const PRELOAD_PROJECT_ATTACHMENTS_EVENT_TYPE = 'PRELOAD_PROJECT_ATTACHMENTS';
export type PreloadProjectAttachmentsEvent = {
    type: typeof PRELOAD_PROJECT_ATTACHMENTS_EVENT_TYPE;
    data: {
        projectId: string;
        userInfo: PatchUserInfo;
        accessToken?: string;
    }
}

export const LOADING_PROJECT_ATTACHMENTS_EVENT_TYPE = 'LOADING_PROJECT_ATTACHMENTS';
export type LoadingProjectAttachmentsEvent = {
    type: typeof LOADING_PROJECT_ATTACHMENTS_EVENT_TYPE;
    data: {
        projectId: string;
        loadedAttachmentsCount: number;
        totalAttachmentsCount: number;
        status?: 'initializing' | 'loading' | 'completed' | 'error';
    }
}

class WebServiceProjectContext extends BaseProjectContext {

    constructor(
        protected readonly dataManager: ProjectContextDataManager,
    ) {
        super();
    }

    protected get _dataManager(): ProjectContextDataManager {
        return this.dataManager;
    }

    public addBlockingTask<T = unknown>(progress: ProgressObserver, key: string, taskFn: (progress: ProgressObserver) => Observable<T>): Observable<T> {
        return of(null);
    }
}

export class PreCacheProjectService {
    constructor(
        private readonly logger: Logger,
        protected readonly projectRepository: ProjectRepository,
        protected fetchProxy: typeof fetch,
    ) {
    }


    public async onPreloadProjectAttachmentsEvent({data: { projectId, userInfo }} : PreloadProjectAttachmentsEvent, source?: MessageEventSource): Promise<void> {
        const progress = voidProgress();
        const isOffline = false;
        console.warn(`[PreCacheProjectService] Starting to preload attachments for project ${projectId}`);
        const dataManager = new SimpleProjectContextManager(
                this.logger,
                this.projectRepository,
                projectId,
                userInfo,
        );
        if (source) {
            source.postMessage({
                type: LOADING_PROJECT_ATTACHMENTS_EVENT_TYPE,
                data: {
                    projectId,
                    loadedAttachmentsCount: 0,
                    totalAttachmentsCount: 0,
                    status: 'initializing',
                },
            });
        }
        const projectContext = new WebServiceProjectContext(dataManager);
        await firstValueFrom(task(() => projectContext.openProjectContext(progress, isOffline)));
        const projectEntity = projectContext.getProjectEntity();
        const maps = projectEntity.maps;
        const documents = projectEntity.ownedDocuments.filter(document => document.downloadPolicy === DOWNLOAD_POLICY.enum.FORCE).map(document => document.activeVersion);
        const bugNotesAnnotations = projectEntity.bugs.flatMap(bug => bug.notes).flatMap(note => [note, ...note.annotations]);
        const bugAnnotations = projectEntity.bugs.flatMap(bug => [bug, ...bug.annotations]);
        const remarksAnnotations = projectEntity.remarks.flatMap(remark => [remark, ...remark.annotations]);
        const attachmentsToPrefetch = [
            projectEntity.getAttachmentForKey('photo.jpg') ?? null,
            ...[...bugNotesAnnotations, ...bugAnnotations, ...remarksAnnotations, ...documents].flatMap(annotation => Object.values(annotation?.getAttachments() ?? {})),
            // all the map attachments: the plan itself (`map.filename`) but also e.g. its plan metadata (`metadata.json`)
            ...maps.flatMap(map => Object.values(map.getAttachments() ?? {})),
        ].filter(isNotNullish);
        console.warn('[PreCacheProjectService] Attachments to prefetch:', attachmentsToPrefetch.length);
        if (source) {
            source.postMessage({
                type: LOADING_PROJECT_ATTACHMENTS_EVENT_TYPE,
                data: {
                    projectId,
                    loadedAttachmentsCount: 0,
                    totalAttachmentsCount: attachmentsToPrefetch.length,
                    status: 'initializing',
                },
            });
        }
        let loadedAttachmentsCount = 0;
        const concurrencyLimit = pLimit(100);
        const fetchAttachments$ = attachmentsToPrefetch.map(async (attachment, index) => 
            concurrencyLimit(async () => {
                if (attachment.type === 'localCache'){
                    // local cache attachments are already cached, no need to fetch them
                    loadedAttachmentsCount++;
                    return;
                }
                try {
                    const url = await firstValueFrom(FileService.url(attachment));
                    if (!url) {
                        return;
                    }
                    console.debug(`[PreCacheProjectService] Prefetching project ${projectId} attachment ${attachment} from url ${url}`);
                    await this.fetchProxy(url);
                } catch (error) {
                    console.error(`[PreCacheProjectService] Failed to prefetch attachment ${attachment} for project ${projectId}`, error);
                }
                loadedAttachmentsCount++;
                if (source  && index % 4 === 0) {
                    source.postMessage({
                        type: LOADING_PROJECT_ATTACHMENTS_EVENT_TYPE,
                        data: {
                            projectId,
                            loadedAttachmentsCount,
                            totalAttachmentsCount: attachmentsToPrefetch.length,
                            status: 'loading',
                        },
                    });
                }
        }));
        await Promise.all(fetchAttachments$);
        if (source) {
            source.postMessage({
                type: LOADING_PROJECT_ATTACHMENTS_EVENT_TYPE,
                data: {
                    projectId,
                    loadedAttachmentsCount: loadedAttachmentsCount,
                    totalAttachmentsCount: attachmentsToPrefetch.length,
                    status: 'completed',
                },
            });
        }
        console.warn(`[PreCacheProjectService] Completed preloading attachments for project ${projectId}`);
        await firstValueFrom(projectContext.close());
        console.warn(`[PreCacheProjectService] Closed project context for project ${projectId}`);
    }

}

export function isPreloadProjectAttachmentsEvent(event: unknown): event is PreloadProjectAttachmentsEvent {
    return event?.['type'] === PRELOAD_PROJECT_ATTACHMENTS_EVENT_TYPE;
}
    
export function  isLoadingProjectAttachmentsEvent(event: unknown): event is LoadingProjectAttachmentsEvent {
    return event?.['type'] === LOADING_PROJECT_ATTACHMENTS_EVENT_TYPE;
}